'use client';

import { Modal, message } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { useTranslation } from '@/i18n/client';
import { EOperationType, type IGroupUserInfo, circleMemberManage } from '@/service/friend';

import RemoveMemberSelect from '../../RemoveMemberSelect';
import styles from './index.less';

// 阻止事件冒泡到父组件
const stopPropagationModalRender = (node: ReactNode) => (
  <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
    {node}
  </div>
);

export interface IRemoveMemberModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  circleRoute: string;
  onSuccess?: () => void;
}

export const RemoveMemberModal = (props: IRemoveMemberModalProps): JSX.Element => {
  const { open, setOpen, circleRoute, onSuccess } = props;
  const { t } = useTranslation();

  const [selectedRemoveMembers, setSelectedRemoveMembers] = useState<IGroupUserInfo[]>([]);
  const [removeLoading, setRemoveLoading] = useState(false);

  // 选择要移除的成员
  const handleSelectRemoveMember = useCallback((member: IGroupUserInfo) => {
    setSelectedRemoveMembers((prev) => {
      const isSelected = prev.some((m) => m.id === member.id);

      if (isSelected) {
        return prev.filter((m) => m.id !== member.id);
      }

      return [...prev, member];
    });
  }, []);

  // 关闭弹窗
  const handleClose = useCallback(() => {
    setOpen(false);
    setSelectedRemoveMembers([]);
  }, [setOpen]);

  // 执行移除成员
  const handleRemoveMembers = useCallback(() => {
    if (selectedRemoveMembers.length === 0) {
      message.warning(t('friend.selectMemberTip'));

      return;
    }

    setRemoveLoading(true);

    const memberIds = selectedRemoveMembers.map((m) => m.id);

    circleMemberManage({
      route: circleRoute,
      operationType: EOperationType.REMOVE,
      userIds: memberIds
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        message.success(t('circle.removeMemberSuccess'));
        handleClose();
        // 刷新群成员列表
        onSuccess?.();
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => {
        setRemoveLoading(false);
      });
  }, [circleRoute, handleClose, onSuccess, selectedRemoveMembers, t]);

  return (
    <Modal
      centered
      className={styles.removeModal}
      closable={false}
      destroyOnClose
      footer={null}
      maskClosable
      modalRender={stopPropagationModalRender}
      onCancel={handleClose}
      open={open}
      width={1000}
      zIndex={1100}
    >
      <RemoveMemberSelect
        circleRoute={circleRoute}
        className={'h-[calc(100vh-64px-32px)]'}
        confirmLoading={removeLoading}
        confirmText={t('common.remove')}
        handleClose={handleClose}
        onConfirm={handleRemoveMembers}
        onSelect={handleSelectRemoveMember}
        open={open}
        selectedIds={selectedRemoveMembers.map((m) => m.id)}
        title={t('circle.removeMember')}
      />
    </Modal>
  );
};

export default RemoveMemberModal;
